import { routes, routes2 } from '@linqpal/models'
import React, { useCallback, useEffect, useState } from 'react'
import { Linking, TouchableOpacity, View } from 'react-native'
import { Divider } from 'react-native-paper'
import BuilderBottomModal from '../../../../ui/molecules/BuilderBottomModal'
import { InputField } from '../../../../ui/atoms/builder-2.0/InputField'
import { PayTermsAndConditions } from '../../../Auth/TermsAndConditions'
import { observer } from 'mobx-react'
import { IconCloseSquare, Left } from '../../../../assets/icons'
import { useInvoiceDetailsContext } from '../../TabInvoice/InvoiceDetailsContext'
import { Spacer } from '../../../../ui/atoms'
import { useStore } from '../../../../store'
import { PaymentPlanCard } from '../../../../ui/atoms/PaymentPlanCard'
import { ChoosePaymentModal } from '../../../../ui/organisms/ChoosePaymentModal'
import Loading from '../../../Loading'
import { isWeb } from '../../../../utils/helpers/commonUtils'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { paths } from '../../../links'
import {
  composeStyle,
  fileDownloader,
} from '@linqpal/common-frontend/src/helpers'
import { useNavigation } from '@react-navigation/native'
import ApplicationStore from '../../../GeneralApplication/Application/ApplicationStore'
import { BtScreen } from '@linqpal/components/src/ui'
import { useResponsive } from '../../../../utils/hooks'
import { useMemo } from 'react'
import { SuccessMessage } from './SuccessMessage'
import { DetailsRowItem } from './DetailsRowItem'
import { PaymentButton } from './PaymentButton'
import { Agreement } from './Agreement'
import { FeeOrDiscount } from './FeeOrDiscount'
import { currencyMask } from '../../../../utils/helpers/masking'

export type PricingPackageType = {
  totalAmount?: string | number
  fee?: string | number
  discount?: number
}

type PayInvoiceModalProps = {
  modal: boolean
  setModal: (val: boolean) => void
  navigation?: any
  invoices: any[]
  account: any
  payWithCredit?: boolean
  paymentPlan?: any
  showModal?: (val: boolean) => void
}

export const PayInvoiceModal = ({
  modal,
  setModal,
  navigation,
  invoices,
  account,
  payWithCredit = false,
  paymentPlan,
  showModal,
}: PayInvoiceModalProps) => {
  const [loading, setLoading] = useState(false)
  const [pricingPackage, setPricingPackage] =
    useState<PricingPackageType | null>(null)
  const invoice = invoices[0]
  const builderCreatedInvoice = invoice?.company_id === ''
  const [paymentMethod, setMethod] = useState(account)
  const [showMethods, setShowMethods] = useState(false)
  const { t } = useTranslation(['errors', 'global'])
  const { sm, screenHeight } = useResponsive()

  const total_amount = invoices.reduce((sum, inv) => sum + inv.total_amount, 0)
  const lateFees = invoices.reduce((sum, inv) => sum + (inv.lateFee || 0), 0)
  const invoiceIds = invoices.map((inv) => inv._id.toString())

  useEffect(() => {
    if (!paymentPlan && invoice && paymentMethod && modal) {
      setLoading(true)
      routes2.invoice
        .getPricingPackageFee({
          invoiceIds: invoices.map((i) => i._id),
          accountId: paymentMethod.id,
        })
        .then((r) => {
          if (r.pricingPackage) {
            const { totalAmount, discount } = r.pricingPackage
            const total =
              paymentMethod?.paymentMethodType === 'bank' && totalAmount
                ? (totalAmount || 0) - (discount || 0)
                : totalAmount
            setPricingPackage({
              ...r.pricingPackage,
              totalAmount: total,
            })
          }
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [invoice, invoices, modal, paymentMethod, paymentPlan, total_amount])

  const onPaymentMethodSelect = useCallback((method) => {
    setLoading(true)
    setShowMethods(false)
    setMethod(method)
  }, [])

  if (payWithCredit) {
    return (
      <PayInvoiceWithCreditModal
        modal={modal}
        setModal={setModal}
        invoiceIds={invoiceIds}
      />
    )
  } else {
    return (
      <>
        <BuilderBottomModal
          visible={modal}
          height={loading ? 600 : screenHeight < 720 ? '100%' : undefined}
          title={t('global:payInvoice.pay-invoice')}
          onClose={() => {
            setModal(false)
          }}
        >
          {loading ? (
            <View style={{ height: 500 }}>
              <Loading />
            </View>
          ) : (
            <>
              {account.newlyAdded && (
                <>
                  <SuccessMessage account={account} />
                  <Spacer height={36} />
                </>
              )}
              <DetailsRowItem
                leftText={`${t('global:payInvoice.invoice-amount')}:`}
                rightText={total_amount}
                money
              />
              <FeeOrDiscount
                paymentMethod={paymentMethod}
                pricingPackage={pricingPackage}
              />

              {!!lateFees && (
                <DetailsRowItem
                  leftText={`${t('global:payInvoice.late-interest-charge')}:`}
                  rightText={lateFees}
                  money
                />
              )}

              <InputField
                label={t('global:payInvoice.total-amount')}
                editable={false}
                model={{
                  totalAmount: currencyMask(
                    (pricingPackage?.totalAmount || total_amount) + lateFees,
                  ),
                }}
                name={'totalAmount'}
                style={{ height: 56 }}
                inputOuterStyle={{ backgroundColor: '#F8F9F9' }}
              />

              <InputField
                label={t('global:payInvoice.To')}
                editable={false}
                model={
                  builderCreatedInvoice
                    ? invoice.supplierInvitationDetails
                    : invoice.company
                }
                name="name"
                style={{ height: 56, color: 'black' }}
                inputOuterStyle={{ backgroundColor: '#F8F9F9' }}
              />
              <InputField
                label={t('global:payInvoice.payment-method')}
                model={{
                  name: (paymentMethod.accountName || paymentMethod.name).split(
                    '/',
                  )[0],
                }}
                name="name"
                style={{ height: 56, marginBottom: 20, color: 'red' }}
                onPress={() => {
                  setShowMethods(true)
                }}
                showDropdown
              />
              <Spacer height={22} />
              <PayTermsAndConditions />
              {!sm ? (
                <Divider
                  style={{
                    marginVertical: 20,
                    height: 1,
                    borderColor: '#F5F7F8',
                    width: '100%',
                  }}
                />
              ) : (
                <Spacer />
              )}
              <PaymentButton
                invoices={invoices}
                account={paymentMethod}
                method={
                  paymentMethod.paymentMethodType === 'bank' ? 'ach' : 'card'
                }
                builderCreatedInvoice={builderCreatedInvoice}
                navigation={navigation}
                closePayModal={() => {
                  setModal(false)
                }}
                loading={loading}
                disabled={false}
              />
              <Spacer height={30} />
            </>
          )}
        </BuilderBottomModal>
        <ChoosePaymentModal
          visible={showMethods}
          onClose={(tab) => {
            tab !== 'Banks' && setModal(false)
            setShowMethods(false)
          }}
          onPress={onPaymentMethodSelect}
          showModal={showModal}
        />
      </>
    )
  }
}

export const PayInvoiceWithCreditModal = ({ modal, setModal, invoiceIds }) => {
  const { t } = useTranslation(['errors', 'global'])
  const { setPaymentPlanModalVisibility } = useInvoiceDetailsContext()
  return (
    <BuilderBottomModal
      visible={modal}
      title={t('global:payInvoice.pay-invoice')}
      onBack={() => {
        setModal(false)
        setPaymentPlanModalVisibility(true)
      }}
      onClose={() => setModal(false)}
      paddingHorizontal={0}
    >
      <PayInvoiceWithCreditContent
        setModal={setModal}
        invoiceIds={invoiceIds}
      />
    </BuilderBottomModal>
  )
}

interface PayInvoiceWithCreditContentProps {
  setModal?: (v: boolean) => void
  invoiceIds: string[]
}

export const PayInvoiceWithCreditContent = observer(
  ({ setModal, invoiceIds }: PayInvoiceWithCreditContentProps) => {
    const { t } = useTranslation('global')
    const {
      invoiceDetails,
      selectedPaymentPlan: paymentPlan,
      bankAccount: account,
      paymentPlans,
    } = ApplicationStore
    const invoice = invoiceDetails[0]
    const builderCreatedInvoice = invoice?.company_id === ''
    const [downloading, setDownloading] = useState(false)
    const navigation = useNavigation()
    const { sm } = useResponsive()
    const plan: any =
      paymentPlan && paymentPlans.find((item) => item._id === paymentPlan?._id)
    const total_amount = invoiceDetails.reduce(
      (sum, inv) => sum + inv.total_amount,
      0,
    )

    let fee, totalAmount
    if (plan) {
      fee = Math.round(total_amount * plan.fee) / 100
      totalAmount = total_amount + fee
    }

    const generateAgreement = () => {
      setDownloading(true)
      routes.contractor
        .makeAgreement({ invoiceIds, paymentPlan: plan })
        .then((r) => {
          if (r.url) {
            if (isWeb) {
              fileDownloader(r.url, {}, r.fileName)
            } else {
              Linking.openURL(r.url)
            }
          }
        })
        .finally(() => {
          setDownloading(false)
        })
    }
    return (
      <>
        <PaymentPlanCard
          onPress={() => {}}
          paymentOption={plan}
          paymentAmount={total_amount}
          selected
          forSummaryPage
        />
        <View style={{ paddingHorizontal: 20 }}>
          <Spacer height={30} />
          <DetailsRowItem
            leftText={t('payInvoice.To')}
            rightText={
              builderCreatedInvoice
                ? invoice.supplierInvitationDetails?.name
                : invoice?.company?.name
            }
          />
          <DetailsRowItem
            leftText={t('payInvoice.invoice-amount')}
            rightText={total_amount}
            money
          />
          <DetailsRowItem
            leftText={t('payInvoice.loan-fee')}
            rightText={fee}
            money
          />
          <DetailsRowItem
            leftText={t('payInvoice.total-amount')}
            rightText={`$ ${totalAmount}`}
            money
          />
          <InputField
            label={t('payInvoice.select-autodebit-account')}
            editable={false}
            model={{
              bankName: (account.accountName || account.name).split('/')[0],
            }}
            name="bankName"
            style={{ height: 56, marginBottom: 20 }}
          />
          {sm && <Spacer height={17} />}
          <Agreement onPress={generateAgreement} downloading={downloading} />
          <Spacer height={sm ? 38 : 20} />
          <PaymentButton
            payWithCredit
            invoices={invoiceDetails}
            account={account}
            builderCreatedInvoice={builderCreatedInvoice}
            method={account?.paymentMethodType === 'bank' ? 'ach' : 'card'}
            navigation={navigation}
            closePayModal={() => {
              if (setModal) setModal(false)
            }}
            loading={false}
            disabled={false}
          />
          <Spacer height={30} />
        </View>
      </>
    )
  },
)

export const PayInvoice = () => {
  const { sm } = useResponsive()
  const { t } = useTranslation('global')
  const navigation = useNavigation()
  const { setPayInvoiceModal, payInvoiceModal, setPaymentPlanModalVisibility } =
    useInvoiceDetailsContext()
  const {
    userStore: { document },
  } = useStore()
  const invoiceArr = useMemo(() => {
    if (document?.invoice_id) {
      return _.isArray(document?.invoice_id)
        ? document?.invoice_id
        : [document?.invoice_id]
    } else {
      return []
    }
  }, [document?.invoice_id])

  if (sm) {
    return (
      <BtScreen
        title={t('payInvoice.pay-invoice')}
        titleCentered
        rightTitleAction={
          <TouchableOpacity
            style={composeStyle(
              {
                borderColor: '#E6EBEE',
                borderRadius: 6,
                borderWidth: 1,
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
              },
              !sm && {
                borderWidth: 0,
                borderRadius: 8,
                backgroundColor: '#F5F7F8',
              },
            )}
            onPress={() => {
              setPayInvoiceModal(false)
              navigation.navigate(paths.Console._self)
            }}
          >
            <IconCloseSquare width={12} height={12} />
          </TouchableOpacity>
        }
        leftTitleAction={
          <TouchableOpacity
            onPress={() => setPaymentPlanModalVisibility(true)}
            style={{
              borderColor: '#E6EBEE',
              borderRadius: 6,
              borderWidth: 1,
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
            }}
          >
            <Left width={12} height={12} fill={'#335C75'} />
          </TouchableOpacity>
        }
      >
        <View style={{ width: 375, alignSelf: 'center' }}>
          <Spacer height={38} />
          <PayInvoiceWithCreditContent invoiceIds={invoiceArr} />
        </View>
      </BtScreen>
    )
  } else {
    return (
      <PayInvoiceWithCreditModal
        modal={payInvoiceModal && !sm}
        setModal={setPayInvoiceModal}
        invoiceIds={invoiceArr}
      />
    )
  }
}
